import { z } from 'zod';

export const Username = z
  .string()
  .min(2)
  .max(32)
  .regex(/^[a-zA-Z0-9_.-]+$/, 'letters, numbers, dot, dash, underscore only');

export const Password = z.string().min(8).max(1024);

export const LoginRequest = z.object({
  username: Username,
  password: Password,
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export const LoginResponse = z.object({
  ok: z.literal(true),
  totpRequired: z.boolean(),
});
export type LoginResponse = z.infer<typeof LoginResponse>;

/** First-boot: create the initial admin user. */
export const SetupRequest = z.object({
  username: Username,
  password: Password,
});
export type SetupRequest = z.infer<typeof SetupRequest>;

export const TotpVerifyRequest = z.object({
  /** 6-digit TOTP code or a recovery code. */
  code: z.string().min(6).max(32),
});
export type TotpVerifyRequest = z.infer<typeof TotpVerifyRequest>;

export const AuthUser = z.object({
  id: z.number().int(),
  username: z.string(),
  displayName: z.string(),
  isAdmin: z.boolean(),
  totpEnabled: z.boolean(),
});
export type AuthUser = z.infer<typeof AuthUser>;

export const AuthState = z.object({
  needsSetup: z.boolean(),
  authenticated: z.boolean(),
  totpPending: z.boolean(),
  user: AuthUser.nullable(),
});
export type AuthState = z.infer<typeof AuthState>;

export const PasswordChangeRequest = z.object({
  current: z.string(),
  next: Password,
});
export type PasswordChangeRequest = z.infer<typeof PasswordChangeRequest>;

export const TotpEnrollRequest = z.object({
  password: z.string().min(1).max(1_024),
});
export type TotpEnrollRequest = z.infer<typeof TotpEnrollRequest>;

export const TotpEnrollResponse = z.object({
  secret: z.string(),
  otpauthUrl: z.string(),
  qrDataUrl: z.string(),
});
export type TotpEnrollResponse = z.infer<typeof TotpEnrollResponse>;

export const TotpConfirmResponse = z.object({
  ok: z.literal(true),
  recoveryCodes: z.array(z.string()),
});
export type TotpConfirmResponse = z.infer<typeof TotpConfirmResponse>;

export const TotpDisableRequest = z.object({
  password: z.string(),
  code: z.string().min(6).max(32),
});
export type TotpDisableRequest = z.infer<typeof TotpDisableRequest>;

// ---- user administration ----

export const UserRow = z.object({
  id: z.number().int(),
  username: z.string(),
  displayName: z.string(),
  isAdmin: z.boolean(),
  totpEnabled: z.boolean(),
  accountCount: z.number().int(),
  createdAt: z.number().int(),
});
export type UserRow = z.infer<typeof UserRow>;
export const UserList = z.array(UserRow);

export const UserPatch = z.object({
  username: Username.optional(),
  displayName: z.string().max(100).optional(),
  isAdmin: z.boolean().optional(),
  newPassword: Password.optional(),
  clearTotp: z.boolean().optional(),
});
export type UserPatch = z.infer<typeof UserPatch>;

// ---- invites ----

export const InviteRow = z.object({
  id: z.number().int(),
  createdAt: z.number().int(),
  expiresAt: z.number().int(),
  usedByUsername: z.string().nullable(),
});
export type InviteRow = z.infer<typeof InviteRow>;
export const InviteList = z.array(InviteRow);

export const InviteCreateResponse = z.object({
  token: z.string(),
  expiresAt: z.number().int(),
});
export type InviteCreateResponse = z.infer<typeof InviteCreateResponse>;

export const InviteCheckResponse = z.object({
  valid: z.boolean(),
});
export type InviteCheckResponse = z.infer<typeof InviteCheckResponse>;

export const InviteAcceptRequest = z.object({
  username: Username,
  password: Password,
});
export type InviteAcceptRequest = z.infer<typeof InviteAcceptRequest>;

export const OkResponse = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof OkResponse>;

export const VersionInfo = z.object({
  sha: z.string(),
  builtAt: z.string().nullable(),
});
export type VersionInfo = z.infer<typeof VersionInfo>;
